import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { QrReader } from 'react-qr-reader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCreditCard, faMoneyBillWave, faTicketAlt, faCalendar, faMapMarkerAlt, faUsers, faQrcode, faTimes } from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import './PurchaseTicketPage.css';
import { CASH_PAYMENTS_ENABLED } from '../config/cashPayments';

const PurchaseTicketPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    iboNumber: '',
    teamName: '',
    eventId: '',
    // DV-313: cash is withdrawn, so card is the only method a new purchase can use.
    paymentMethod: CASH_PAYMENTS_ENABLED ? 'CASH' : 'SQUARE'
  });
  
  // Ticket selection state
  const [selectedTickets, setSelectedTickets] = useState([]);
  
  // UI state
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]); // Store all events before filtering
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEvents, setFetchingEvents] = useState(true);
  const [fetchingTeams, setFetchingTeams] = useState(true);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  // QR Scanner state
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scannerKey, setScannerKey] = useState(Date.now());
  const activeStreamRef = useRef(null);
  
  // User data state
  const [userData, setUserData] = useState(null);
  const [fetchingUserData, setFetchingUserData] = useState(false);
  const [iboValidated, setIboValidated] = useState(false);
  
  // Payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentToken, setPaymentToken] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  
  // Fetch user data by IBO number
  const fetchUserData = async (iboNumber) => {
    try {
      setFetchingUserData(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user/get?iboNumber=${iboNumber}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch user data. Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setUserData(result.data);
        setIboValidated(true);
        
        // Update teamName from user data
        if (result.data.teamName) {
          setFormData(prev => ({
            ...prev,
            teamName: result.data.teamName
          }));
          
          // Filter events based on user's team
          filterEventsByTeam(result.data.teamName);
        } else {
          // If no team, show all events with "all" eligibility
          filterEventsByTeam(null);
        }
        
        setFormErrors(prev => ({ ...prev, iboNumber: null }));
      } else {
        throw new Error(result.message || 'Failed to load user data');
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(`Failed to load user data: ${err.message}`);
      setIboValidated(false);
      setUserData(null);
    } finally {
      setFetchingUserData(false);
    }
  };
  
  // Filter events based on team eligibility
  const filterEventsByTeam = (teamName) => {
    if (!allEvents || allEvents.length === 0) return;
    
    let filtered = allEvents.filter(event => {
      // Check if event has eligibleTeams field
      if (!event.eligibleTeams || event.eligibleTeams.length === 0) {
        return true; // Show if no eligibility restriction
      }
      
      // Check if "all" is in eligible teams
      if (event.eligibleTeams.some(team => team.toLowerCase() === 'all')) {
        return true;
      }
      
      // Check if user's team matches
      if (teamName && event.eligibleTeams.some(team => 
        team.toLowerCase() === teamName.toLowerCase()
      )) {
        return true;
      }
      
      return false;
    });
    
    // Sort events by date (recent first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateA - dateB; // Sort ascending (upcoming events first)
    });
    
    setEvents(filtered);
    
    // Auto-select the first event if available
    if (filtered.length > 0) {
      setFormData(prev => ({
        ...prev,
        eventId: filtered[0].id
      }));
    }
  };
  
  // Fetch events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setFetchingEvents(true);
        
        const token = await getAccessToken();
        if (!token) {
          throw new Error('Authentication token not available');
        }
        
        const response = await fetch(`${config.API_BASE_URL}/em/v1/events/upcoming`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch events. Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.status === 'success' && result.data) {
          // DV-334: an event that has ended cannot be sold; the server refuses it too. Its status
          // stays UPCOMING until the nightly completion job runs a day later, so filter here.
          const now = new Date();
          const openEvents = result.data.filter(event => !event.endDate || new Date(event.endDate) >= now);
          // Sort events by date (recent/upcoming first)
          const sortedEvents = openEvents.sort((a, b) => {
            const dateA = new Date(a.startDate);
            const dateB = new Date(b.startDate);
            return dateA - dateB;
          });
          
          setAllEvents(sortedEvents);
          setEvents(sortedEvents); // Initially show all events
        } else {
          throw new Error(result.message || 'Failed to load events');
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(`Failed to load events: ${err.message}`);
      } finally {
        setFetchingEvents(false);
      }
    };
    
    fetchEvents();
  }, [getAccessToken]);
  
  // Fetch teams on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setFetchingTeams(true);
        
        const token = await getAccessToken();
        if (!token) {
          throw new Error('Authentication token not available');
        }
        
        const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch teams. Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.status === 'success' && result.data) {
          setTeams(result.data);
          
          // Auto-select the first team if available
          if (result.data.length > 0) {
            setFormData(prev => ({
              ...prev,
              teamName: result.data[0].name
            }));
          }
        } else {
          throw new Error(result.message || 'Failed to load teams');
        }
      } catch (err) {
        console.error('Error fetching teams:', err);
        setError(`Failed to load teams: ${err.message}`);
      } finally {
        setFetchingTeams(false);
      }
    };
    
    fetchTeams();
  }, [getAccessToken]);
  
  // Get selected event
  const selectedEvent = events.find(event => event.id === parseInt(formData.eventId));
  
  // Handle QR code scan
  const handleQRScan = (result) => {
    if (result) {
      // Extract IBO number from QR code
      let iboNumber = result.text || result;
      
      // Clean up the IBO number (remove any prefixes like T-, GT-, etc.)
      if (iboNumber.startsWith('T-') || iboNumber.startsWith('GT-')) {
        iboNumber = iboNumber.substring(iboNumber.indexOf('-') + 1);
      }
      
      // Remove any non-numeric characters
      iboNumber = iboNumber.replace(/\D/g, '');
      
      if (iboNumber) {
        setFormData(prev => ({
          ...prev,
          iboNumber: iboNumber
        }));
        
        // Close scanner and fetch user data
        closeQRScanner();
        fetchUserData(iboNumber);
      }
    }
  };
  
  // Open QR Scanner
  const openQRScanner = () => {
    setShowQRScanner(true);
    setScannerKey(Date.now()); // Reset scanner
  };
  
  // Close QR Scanner
  const closeQRScanner = () => {
    // Stop all media streams
    if (activeStreamRef.current) {
      const tracks = activeStreamRef.current.getTracks();
      tracks.forEach(track => track.stop());
      activeStreamRef.current = null;
    }
    
    setShowQRScanner(false);
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activeStreamRef.current) {
        const tracks = activeStreamRef.current.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error if field is filled
    if (value.trim()) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Reset ticket selection when event changes
    if (name === 'eventId') {
      setSelectedTickets([]);
    }
    
    // If IBO number changes, reset validation
    if (name === 'iboNumber') {
      setIboValidated(false);
      setUserData(null);
    }
  };
  
  // Handle IBO number validation/fetch
  const handleIBOValidation = () => {
    if (formData.iboNumber && formData.iboNumber.trim()) {
      fetchUserData(formData.iboNumber.trim());
    }
  };
  
  // Handle ticket selection
  const handleTicketChange = (ticketPriceInfoId, quantity) => {
    setSelectedTickets(prev => {
      const existingIndex = prev.findIndex(ticket => ticket.ticketPriceInfoId === ticketPriceInfoId);
      
      if (quantity === 0) {
        // Remove ticket if quantity is 0
        return prev.filter(ticket => ticket.ticketPriceInfoId !== ticketPriceInfoId);
      }
      
      if (existingIndex >= 0) {
        // Update existing ticket
        const updated = [...prev];
        updated[existingIndex] = { ticketPriceInfoId, numberOfTickets: quantity };
        return updated;
      } else {
        // Add new ticket
        return [...prev, { ticketPriceInfoId, numberOfTickets: quantity }];
      }
    });
  };
  
  // Calculate total amount
  const calculateTotal = () => {
    if (!selectedEvent || selectedTickets.length === 0) return 0;
    
    let total = 0;
    selectedTickets.forEach(selectedTicket => {
      const ticketInfo = selectedEvent.ticketPriceInfos.find(
        info => info.id === selectedTicket.ticketPriceInfoId
      );
      if (ticketInfo) {
        const price = getEffectivePrice(ticketInfo);
        const subtotal = price * selectedTicket.numberOfTickets;
        const tax = (subtotal * ticketInfo.taxPercentage) / 100;
        const serviceCharge = ticketInfo.serviceCharge * selectedTicket.numberOfTickets;
        total += subtotal + tax + serviceCharge;
      }
    });
    
    return total;
  };
  
  // Get effective price (considering early bird and group discounts)
  const getEffectivePrice = (ticketInfo) => {
    const now = new Date();
    
    // Check early bird pricing
    if (ticketInfo.earlyBirdPrice && ticketInfo.earlyBirdEndDate) {
      const earlyBirdEnd = new Date(
        ticketInfo.earlyBirdEndDate[0],
        ticketInfo.earlyBirdEndDate[1] - 1,
        ticketInfo.earlyBirdEndDate[2],
        ticketInfo.earlyBirdEndDate[3] || 23,
        ticketInfo.earlyBirdEndDate[4] || 59,
        ticketInfo.earlyBirdEndDate[5] || 59
      );
      
      if (now <= earlyBirdEnd) {
        return ticketInfo.earlyBirdPrice;
      }
    }
    
    // Check group discount
    if (ticketInfo.groupDiscountPrice && ticketInfo.minimumGroupSize) {
      const selectedTicket = selectedTickets.find(t => t.ticketPriceInfoId === ticketInfo.id);
      if (selectedTicket && selectedTicket.numberOfTickets >= ticketInfo.minimumGroupSize) {
        return ticketInfo.groupDiscountPrice;
      }
    }
    
    return ticketInfo.price;
  };
  
  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.iboNumber.trim()) {
      errors.iboNumber = 'IBO Number is required';
    } else if (!/^\d+$/.test(formData.iboNumber)) {
      errors.iboNumber = 'IBO Number must contain only numbers';
    }
    
    if (!formData.eventId) {
      errors.eventId = 'Please select an event';
    }
    
    if (selectedTickets.length === 0) {
      errors.tickets = 'Please select at least one ticket';
    }
    
    if (!formData.paymentMethod) {
      errors.paymentMethod = 'Please select a payment method';
    }
    
    if (formData.paymentMethod === 'SQUARE' && !cardholderName.trim()) {
      errors.cardholderName = 'Cardholder name is required for card payments';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle payment method change
  const handlePaymentMethodChange = (method) => {
    setFormData(prev => ({ ...prev, paymentMethod: method }));
    setShowPaymentForm(method === 'SQUARE');
    setPaymentToken(null);
    setFormErrors(prev => ({ ...prev, paymentMethod: null }));
  };
  
  // Handle Square payment token
  const handleSquareTokenReceived = async (token, verifiedBuyer) => {
    console.log('Square token received:', token);
    setPaymentToken(token.token);
    setError(null);
    
    // Automatically submit the form when token is received
    setTimeout(() => {
      handleFormSubmission(token.token);
    }, 100);
  };
  
  // Separate form submission logic
  const handleFormSubmission = async (squareToken = null) => {
    if (!validateForm()) {
      return;
    }
    
    // For Square payments, ensure we have a token
    if (formData.paymentMethod === 'SQUARE' && !squareToken && !paymentToken) {
      setError('Please complete the card payment form');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Prepare data for API
      const purchaseData = {
        iboNumber: formData.iboNumber,
        teamName: formData.teamName,
        eventId: parseInt(formData.eventId, 10),
        paymentMethod: formData.paymentMethod,
        selectedTickets: selectedTickets
      };
      
      // Add Square-specific fields if needed
      if (formData.paymentMethod === 'SQUARE') {
        purchaseData.paymentToken = squareToken || paymentToken;
        purchaseData.cardholderName = cardholderName;
      }
      
      const response = await fetch(`${config.API_BASE_URL}/em/v1/ticket/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(purchaseData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `Error: ${response.status}`);
      }
      
      // Success! Show confirmation
      setSuccessData(data.data);
      
    } catch (err) {
      console.error('Error purchasing ticket:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Navigate back to dashboard
  const handleBack = () => {
    navigate('/');
  };
  
  // Purchase another ticket (reset success state)
  const handlePurchaseAnother = () => {
    setSuccessData(null);
    setSelectedTickets([]);
    setPaymentToken(null);
    setCardholderName('');
    setShowPaymentForm(false);
    setFormData({
      iboNumber: '',
      teamName: teams.length > 0 ? teams[0].name : '',
      eventId: events.length > 0 ? events[0].id : '',
      // DV-313: resetting the form must not quietly put cash back.
      paymentMethod: CASH_PAYMENTS_ENABLED ? 'CASH' : 'SQUARE'
    });
  };
  
  // Format date from date string
  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    
    try {
      // Handle date formats with timezone abbreviations like "2025-11-20 18:00:00 PST"
      // Safari doesn't parse timezone abbreviations, so we need to parse manually
      let dateStr = String(dateString).trim();
      
      // Remove timezone abbreviations (PST, PDT, EST, EDT, etc.)
      dateStr = dateStr.replace(/\s+(PST|PDT|EST|EDT|CST|CDT|MST|MDT|GMT|UTC)$/i, '');
      
      // Parse the date components manually for maximum Safari compatibility
      // Expected format after removing timezone: "2025-11-20 18:00:00"
      const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
      
      let date;
      if (match) {
        // Create date from components (month is 0-indexed in JavaScript)
        const [, year, month, day, hour, minute, second] = match;
        date = new Date(
          parseInt(year, 10),
          parseInt(month, 10) - 1, // Month is 0-indexed
          parseInt(day, 10),
          parseInt(hour, 10),
          parseInt(minute, 10),
          parseInt(second, 10)
        );
      } else {
        // Fallback: try replacing space with 'T' for ISO format
        dateStr = dateStr.replace(' ', 'T');
        date = new Date(dateStr);
      }
      
      // Validate the date
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', dateString);
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid Date';
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For cash payments, submit immediately
    if (formData.paymentMethod === 'CASH') {
      handleFormSubmission();
    }
    // For Square payments, the form will be submitted automatically when token is received
  };
  
  // If events are loading, show a loading indicator
  if (fetchingEvents) {
    return (
      <div className="purchase-container loading-container">
        <h2>Loading Events...</h2>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // If ticket sale was successful, show confirmation
  if (successData) {
    return (
      <div className="purchase-container">
        <div className="purchase-success">
          <h2>🎉 Ticket Sold Successfully!</h2>
          <div className="success-details">
            <div className="confirmation-code">
              <span>Confirmation Code:</span>
              <strong>{successData.purchaseConfirmationCode}</strong>
            </div>
            
            <div className="purchase-summary">
              <div className="summary-row">
                <span>Event:</span>
                <span>{successData.eventName}</span>
              </div>
              <div className="summary-row">
                <span>IBO Number:</span>
                <span>{successData.iboNumber}</span>
              </div>
              {successData.teamName && (
                <div className="summary-row">
                  <span>Team:</span>
                  <span>{successData.teamName}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Total Tickets:</span>
                <span>{successData.totalTickets}</span>
              </div>
              <div className="summary-row">
                <span>Payment Method:</span>
                <span>{successData.paymentMethod}</span>
              </div>
              <div className="summary-row total">
                <span>Total Amount:</span>
                <span>{formatCurrency(successData.total)}</span>
              </div>
            </div>
            
            {successData.tickets && successData.tickets.length > 0 && (
              <div className="tickets-breakdown">
                <h4>Ticket Details:</h4>
                {successData.tickets.map((ticket, index) => (
                  <div key={index} className="ticket-item">
                    <span>{ticket.ticketName} (x{ticket.quantity})</span>
                    <span>{formatCurrency(ticket.totalPrice)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="success-actions">
            <button 
              onClick={handlePurchaseAnother}
              className="purchase-button"
            >
              Sell Another Ticket
            </button>
            <button 
              onClick={handleBack}
              className="back-button"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Render the sell ticket form
  return (
    <div className="purchase-container">
      <div className="page-header">
        <button onClick={handleBack} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Dashboard
        </button>
        <h2>Sell Ticket</h2>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {/* QR Scanner Modal */}
      {showQRScanner && (
        <div className="qr-scanner-modal">
          <div className="qr-scanner-content">
            <div className="qr-scanner-header">
              <h3>Scan QR Code</h3>
              <button 
                type="button" 
                onClick={closeQRScanner} 
                className="close-scanner-btn"
                aria-label="Close scanner"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="qr-reader-container">
              <QrReader
                key={scannerKey}
                constraints={{
                  facingMode: 'environment',
                  width: { ideal: 1280 },
                  height: { ideal: 720 }
                }}
                onResult={(result, error) => {
                  if (result) {
                    handleQRScan(result);
                  }
                }}
                className="qr-reader"
                scanDelay={300}
                videoStyle={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <div className="scanner-overlay">
                <div className="scanner-target"></div>
              </div>
            </div>
            
            <p className="scanner-instructions">
              Position the QR code within the frame to scan
            </p>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="purchase-form">
        {/* IBO Number */}
        <div className="form-group">
          <label htmlFor="iboNumber">IBO Number *</label>
          <div className="ibo-input-group">
            <input
              type="text"
              id="iboNumber"
              name="iboNumber"
              value={formData.iboNumber}
              onChange={handleChange}
              placeholder="Enter IBO Number"
              className={formErrors.iboNumber ? 'error' : ''}
              disabled={fetchingUserData}
            />
            <button
              type="button"
              onClick={openQRScanner}
              className="qr-scan-button"
              disabled={fetchingUserData}
              title="Scan QR Code"
            >
              <FontAwesomeIcon icon={faQrcode} />
            </button>
            {formData.iboNumber && !iboValidated && (
              <button
                type="button"
                onClick={handleIBOValidation}
                className="validate-button"
                disabled={fetchingUserData}
              >
                {fetchingUserData ? 'Validating...' : 'Validate'}
              </button>
            )}
          </div>
          {formErrors.iboNumber && <div className="error-text">{formErrors.iboNumber}</div>}
          {iboValidated && userData && (
            <div className="user-info-badge">
              ✓ Valid IBO: {userData.iboName || userData.email}
            </div>
          )}
        </div>
        
        {/* Team Name */}
        <div className="form-group">
          <label htmlFor="teamName">Team Name</label>
          <input
            type="text"
            id="teamName"
            name="teamName"
            value={formData.teamName}
            onChange={handleChange}
            placeholder="Team Name (auto-filled from user profile)"
            className={formErrors.teamName ? 'error' : ''}
            readOnly={iboValidated && userData && userData.teamName}
            disabled={!iboValidated}
          />
          {formErrors.teamName && <div className="error-text">{formErrors.teamName}</div>}
          {iboValidated && userData && userData.teamName && (
            <div className="info-text">Team automatically set from user profile</div>
          )}
          {iboValidated && userData && !userData.teamName && (
            <div className="info-text">No team associated with this user</div>
          )}
        </div>
        
        {/* Event Selection */}
        <div className="form-group">
          <label htmlFor="eventId">Select Event *</label>
          <select
            id="eventId"
            name="eventId"
            value={formData.eventId}
            onChange={handleChange}
            className={formErrors.eventId ? 'error' : ''}
          >
            <option value="">-- Select an Event --</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.name} ({formatDate(event.startDate)})
              </option>
            ))}
          </select>
          {formErrors.eventId && <div className="error-text">{formErrors.eventId}</div>}
          
          {events.length === 0 && !fetchingEvents && (
            <div className="info-text">No upcoming events available</div>
          )}
        </div>
        
        {/* Event Details */}
        {selectedEvent && (
          <div className="event-details">
            <h3>
              <FontAwesomeIcon icon={faTicketAlt} />
              {selectedEvent.name}
            </h3>
            <div className="event-info">
              <div className="event-info-item">
                <FontAwesomeIcon icon={faCalendar} />
                <span>{formatDate(selectedEvent.startDate)} - {formatDate(selectedEvent.endDate)}</span>
              </div>
              <div className="event-info-item">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>{selectedEvent.location}</span>
              </div>
            </div>
            <p className="event-description">{selectedEvent.description}</p>
          </div>
        )}
        
        {/* Ticket Selection */}
        {selectedEvent && selectedEvent.ticketPriceInfos && selectedEvent.ticketPriceInfos.length > 0 && (
          <div className="ticket-selection">
            <h4>Select Tickets</h4>
            {selectedEvent.ticketPriceInfos
              .filter(ticket => ticket.active)
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map(ticketInfo => {
                const effectivePrice = getEffectivePrice(ticketInfo);
                const selectedTicket = selectedTickets.find(t => t.ticketPriceInfoId === ticketInfo.id);
                const quantity = selectedTicket ? selectedTicket.numberOfTickets : 0;
                
                return (
                  <div key={ticketInfo.id} className="ticket-option">
                    <div className="ticket-info">
                      <div className="ticket-header">
                        <h5>{ticketInfo.name}</h5>
                        <div className="ticket-price">
                          {effectivePrice !== ticketInfo.price && (
                            <span className="original-price">{formatCurrency(ticketInfo.price)}</span>
                          )}
                          <span className="current-price">{formatCurrency(effectivePrice)}</span>
                        </div>
                      </div>
                      <p className="ticket-description">{ticketInfo.description}</p>
                      
                      <div className="ticket-details">
                        <span className="ticket-type">{ticketInfo.ticketType}</span>
                        {ticketInfo.taxPercentage > 0 && (
                          <span className="tax-info">+{ticketInfo.taxPercentage}% tax</span>
                        )}
                        {ticketInfo.serviceCharge > 0 && (
                          <span className="service-charge">+{formatCurrency(ticketInfo.serviceCharge)} service charge</span>
                        )}
                      </div>
                      
                      {/* Early bird or group discount indicators */}
                      {effectivePrice < ticketInfo.price && (
                        <div className="discount-info">
                          {ticketInfo.earlyBirdPrice === effectivePrice && (
                            <span className="early-bird">🎉 Early Bird Price!</span>
                          )}
                          {ticketInfo.groupDiscountPrice === effectivePrice && (
                            <span className="group-discount">👥 Group Discount Applied!</span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="ticket-quantity">
                      <label>Quantity:</label>
                      <input
                        type="number"
                        min="0"
                        max={Math.min(ticketInfo.availableQuantity, 10)}
                        value={quantity}
                        onChange={(e) => handleTicketChange(ticketInfo.id, parseInt(e.target.value) || 0)}
                        className="quantity-input"
                      />
                      <span className="available-qty">({ticketInfo.availableQuantity} available)</span>
                    </div>
                  </div>
                );
              })}
            {formErrors.tickets && <div className="error-text">{formErrors.tickets}</div>}
          </div>
        )}
        
        {/* Order Summary */}
        {selectedTickets.length > 0 && (
          <div className="order-summary">
            <h4>Order Summary</h4>
            <div className="summary-items">
              {selectedTickets.map(selectedTicket => {
                const ticketInfo = selectedEvent.ticketPriceInfos.find(
                  info => info.id === selectedTicket.ticketPriceInfoId
                );
                if (!ticketInfo) return null;
                
                const effectivePrice = getEffectivePrice(ticketInfo);
                const subtotal = effectivePrice * selectedTicket.numberOfTickets;
                const tax = (subtotal * ticketInfo.taxPercentage) / 100;
                const serviceCharge = ticketInfo.serviceCharge * selectedTicket.numberOfTickets;
                const total = subtotal + tax + serviceCharge;
                
                return (
                  <div key={selectedTicket.ticketPriceInfoId} className="summary-item">
                    <div className="item-details">
                      <span className="item-name">{ticketInfo.name} (x{selectedTicket.numberOfTickets})</span>
                      <span className="item-price">{formatCurrency(subtotal)}</span>
                    </div>
                    {tax > 0 && (
                      <div className="item-details sub-item">
                        <span>Tax ({ticketInfo.taxPercentage}%)</span>
                        <span>{formatCurrency(tax)}</span>
                      </div>
                    )}
                    {serviceCharge > 0 && (
                      <div className="item-details sub-item">
                        <span>Service Charge</span>
                        <span>{formatCurrency(serviceCharge)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              
              <div className="summary-total">
                <span>Total Amount:</span>
                <span>{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Payment Method Selection */}
        {selectedTickets.length > 0 && (
          <div className="payment-section">
            <h4>Payment Method</h4>
            <div className="payment-methods">
              {/* DV-313: cash is no longer accepted. Kept behind the flag rather than deleted
                  so restoring it is one constant, not an archaeology exercise. */}
              {CASH_PAYMENTS_ENABLED && (
                <button
                  type="button"
                  className={`payment-method-button ${formData.paymentMethod === 'CASH' ? 'active' : ''}`}
                  onClick={() => handlePaymentMethodChange('CASH')}
                >
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                  <span>Pay with Cash</span>
                </button>
              )}

              <button
                type="button"
                className={`payment-method-button ${formData.paymentMethod === 'SQUARE' ? 'active' : ''}`}
                onClick={() => handlePaymentMethodChange('SQUARE')}
              >
                <FontAwesomeIcon icon={faCreditCard} />
                <span>Pay with Card</span>
              </button>
            </div>
            {formErrors.paymentMethod && <div className="error-text">{formErrors.paymentMethod}</div>}
            
            {/* Square Payment Form */}
            {showPaymentForm && formData.paymentMethod === 'SQUARE' && (
              <div className="square-payment-form">
                <div className="form-group">
                  <label htmlFor="cardholderName">Cardholder Name *</label>
                  <input
                    type="text"
                    id="cardholderName"
                    value={cardholderName}
                    onChange={(e) => setCardholderName(e.target.value)}
                    placeholder="Enter cardholder name"
                    className={formErrors.cardholderName ? 'error' : ''}
                    disabled={loading}
                  />
                  {formErrors.cardholderName && <div className="error-text">{formErrors.cardholderName}</div>}
                </div>
                
                {loading ? (
                  <div className="square-loading">
                    <div className="loading-spinner"></div>
                    <p>Processing your payment...</p>
                  </div>
                ) : (
                  <PaymentForm
                    applicationId={config.SQUARE_APPLICATION_ID}
                    locationId={config.SQUARE_LOCATION_ID}
                    cardTokenizeResponseReceived={handleSquareTokenReceived}
                    createPaymentRequest={() => ({
                      countryCode: 'US',
                      currencyCode: 'USD',
                      total: {
                        amount: calculateTotal().toFixed(2),
                        label: 'Total'
                      }
                    })}
                  >
                    <CreditCard
                      buttonProps={{
                        css: {
                          backgroundColor: '#007bff',
                          fontSize: '16px',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#0056b3'
                          }
                        }
                      }}
                    />
                  </PaymentForm>
                )}
              </div>
            )}
          </div>
        )}
        
        <div className="form-actions">
          <button
            type="submit"
            className="purchase-button"
            disabled={loading || selectedTickets.length === 0 || !iboValidated || (formData.paymentMethod === 'SQUARE' && showPaymentForm)}
            style={{ display: (formData.paymentMethod === 'SQUARE' && showPaymentForm) ? 'none' : 'flex' }}
          >
            {loading ? 'Processing...' : `Sell Tickets - ${formatCurrency(calculateTotal())}`}
          </button>
          
          <button
            type="button"
            onClick={handleBack}
            className="cancel-button"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PurchaseTicketPage; 